import { Toast } from '../../../components/Common/Toast';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { callAPI } from '../../../services/api.service';

export interface CleverClassroom {
  id: string;
  name: string;
  section: string;
  teacher: string;
  school: string;
  grade: string;
  subject: string;
  period: string;
  lastModified: string;
}

export interface CleverStudent {
  id: string;
  name: {
    first: string;
    last: string;
  };
  email?: string;
  grade?: string;
  school: string;
  lastModified: string;
}

/**
 * Get teacher's Clever classrooms using server-managed token
 * @returns List of Clever classrooms or null if failed
 */
export const getCleverClassrooms = async (): Promise<CleverClassroom[] | null> => {
  try {
    const response = await callAPI('api/clever/classrooms', null, 'GET');

    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to fetch Clever classrooms', true);
      return null;
    }

    return response.classrooms || [];
  } catch (error) {
    Toast('Failed to fetch Clever classrooms', true);
    return null;
  }
};

/**
 * Get students from a specific Clever classroom using server-managed token
 * @param cleverClassroomId Clever classroom ID
 * @param classroomId Clever classroom ID
 * @returns List of Clever students or null if failed
 */
export const getCleverClassroomStudents = async (
  cleverClassroomId: string,
  classroomId: string,
): Promise<CleverStudent[] | null> => {
  try {
    const response = await callAPI(`api/clever/classrooms/${cleverClassroomId}/students?classroomId=${classroomId}`, null, 'GET');

    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to fetch Clever students', true);
      return null;
    }

    return response.students || [];
  } catch (error) {
    Toast('Failed to fetch Clever students', true);
    return null;
  }
};

/**
 * Import students from Clever classroom to Build My Story classroom using server-managed token
 * @param cleverClassroomId Clever classroom ID
 * @param targetClassroomId Target Build My Story classroom ID
 * @param targetProjectId Target project ID
 * @param selectedStudentIds Array of selected student IDs
 * @param isOnboarding
 * @returns API response or null if failed
 */
export const importCleverStudents = async (
  cleverClassroomId: string,
  targetClassroomId: string,
  targetProjectId: string,
  selectedStudentIds: string[],
  isOnboarding: boolean,
): Promise<any> => {
  try {
    const response = await callAPI(
      `api/clever/classrooms/${cleverClassroomId}/import`,
      {
        targetClassroomId,
        targetProjectId,
        selectedStudentIds,
        isOnboarding: !!isOnboarding,
      },
      'POST',
    );

    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Students successfully imported from Clever', false);
    } else {
      Toast(response?.error || 'Failed to import students from Clever', true);
    }

    return response;
  } catch (error) {
    Toast('Failed to import students from Clever', true);
    return null;
  }
};
